// Quoting API helper (Lane 1335) over the WS6 routes (plan Appendix O).
// The server is the only source of every number; the page never recomputes
// one. A pick, an account change and a line read always send the account
// revision (and a pick the read generation) the page showed, so an old page
// cannot restore a cleared pick or overwrite newer accounts (409 instead).
import { useMemo, useRef } from 'react';
import { API_BASE } from '../../config';
import { useAuth } from '../../AuthContext';
import { requestChatTurn } from '../../utils/chatProgressStream';

export class QuotingError extends Error {
  constructor(status, detail) {
    const message = (detail && typeof detail === 'object' && detail.message)
      || (typeof detail === 'string' && status !== 500 ? detail : 'Something went wrong. Try again.');
    super(message);
    this.status = status;
    this.code = detail && typeof detail === 'object' ? detail.code : undefined;
  }
}

async function parse(response) {
  let body = null;
  try { body = await response.json(); } catch (e) { body = null; }
  if (!response.ok) throw new QuotingError(response.status, body && body.detail);
  return body;
}

export function makeQuotingApi(authFetch, authHeader = () => ({})) {
  const base = `${API_BASE}/api/rfqs`;
  const get = (path, params) => {
    const clean = Object.entries(params || {}).filter(([, v]) => v !== undefined && v !== null && v !== '');
    const qs = clean.length ? `?${new URLSearchParams(clean)}` : '';
    return authFetch(`${base}${path}${qs}`).then(parse);
  };
  const post = (path, body) => authFetch(`${base}${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  }).then(parse);
  return {
    list: (cursor) => get('', { cursor }),
    rfq: (rfqId) => get(`/${rfqId}`),
    line: (rfqId, lineId, revision, cursors = {}) => get(`/${rfqId}/lines/${lineId}`, { revision, ...cursors }),
    accounts: (rfqId, q, page) => get(`/${rfqId}/accounts`, { ...(q === undefined ? {} : { q }), page }),
    setAccounts: (rfqId, customerCodes, revisionSeen) => post(`/${rfqId}/customer`, {
      customer_codes: customerCodes, revision_seen: revisionSeen,
    }),
    rematch: (rfqId) => post(`/${rfqId}/rematch`),
    pick: (rfqId, lineId, materialNumber, rfq) => post(`/${rfqId}/lines/${lineId}/pick`, {
      material_number: materialNumber,
      revision_seen: rfq.account_revision || 0,
      generation_seen: rfq.published_generation || 0,
    }),
    searchParts: (q) => get('/part-search', { q }),
    attachmentUrl: (rfqId, attachmentId) => `${base}/${rfqId}/attachments/${attachmentId}`,
    download: (rfqId, attachmentId) => authFetch(`${base}/${rfqId}/attachments/${attachmentId}`)
      .then(async (r) => { if (!r.ok) throw new QuotingError(r.status, null); return r.blob(); }),
    // The side panel (Lane 1399): how to show a file, and a TIFF page as PNG.
    view: (rfqId, attachmentId) => get(`/${rfqId}/attachments/${attachmentId}/view`),
    tiffPage: (rfqId, attachmentId, page) => authFetch(`${base}/${rfqId}/attachments/${attachmentId}/page?page=${page}`)
      .then(async (r) => { if (!r.ok) throw new QuotingError(r.status, null); return r.blob(); }),
    // The Wingman chat about one RFQ (Lane 1376 P1). Each RFQ has its own
    // conversation; the turn streams, with the plain POST as the fallback, and
    // the same client_turn_id makes a retry a replay.
    chat: (rfqId) => get(`/${rfqId}/chat`),
    newConversation: (rfqId) => post(`/${rfqId}/chat/conversations`),
    chatTurn: (rfqId, body, onEvent) => requestChatTurn({
      streamUrl: `${base}/${rfqId}/chat/turns/stream`,
      fallbackUrl: `${base}/${rfqId}/chat/turns`,
      headers: { 'Content-Type': 'application/json', ...authHeader() },
      body,
      onEvent,
    }),
  };
}

export function useQuotingApi() {
  const { authFetch, getAuthHeader } = useAuth();
  const header = useRef(getAuthHeader);
  header.current = getAuthHeader;
  return useMemo(() => makeQuotingApi(authFetch, () => (header.current ? header.current() : {})),
    [authFetch]);
}
